import * as React from 'react';
import { cn } from '@/shared/lib/cn';
import { avatarColor, initialsOf } from '@/shared/lib/avatar';
import { resolveApiUrl } from '@/shared/lib/config';
import styles from './Avatar.module.css';

export interface AvatarProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  name: string;
  seed: string;
  src?: string | null;
}

/**
 * Аватар человека: своя фотография, а без неё — инициалы на градиенте.
 *
 * Размер, скругление и кегль инициалов задаёт место вызова своим `className`:
 * аватар живёт в двух десятках мест разного масштаба, от строки таймлайна до
 * шапки профиля, и у каждого своя мера. Компонент отвечает только за то, ЧТО
 * внутри круга.
 *
 * Фото, которое не загрузилось (истекла ссылка, файл снят), тихо уступает
 * место инициалам — пустой круг читается как сбой, а инициалы — как человек.
 *
 * @use аватар человека в любом месте — вместо своего span с `initialsOf`/`avatarColor`; геометрию задаёт `className`.
 */
export const Avatar = React.forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { name, seed, src, className, style, ...rest },
  ref,
) {
  const [failed, setFailed] = React.useState<string | null>(null);
  const showPhoto = Boolean(src) && failed !== src;
  return (
    <span
      ref={ref}
      className={cn(styles.avatar, className)}
      style={showPhoto ? style : { background: avatarColor(seed), ...style }}
      aria-hidden={rest['aria-label'] ? undefined : true}
      {...rest}
    >
      {showPhoto ? (
        <img
          className={styles.photo}
          src={src ? resolveApiUrl(src) : undefined}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailed(src ?? null)}
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
});
